import * as React from 'react';
import { translate, InjectedTranslateProps } from 'react-i18next';
import {
  Text,
  Heading,
  Button,
  ToggleSwitch,
  Modal,
  MessageBoxFunctionalLayout,
  TextButton,
} from 'wix-style-react';
import { ChannelFilter } from '../../../../consts/channel-filter';
import { setUserSettings } from '../../../../apis/settings-server/settings-server';
import * as browserNotificationsClient from '../../../../apis/browser-notifications/browser-notifications';
import { provideLogger, InjectedBiLoggerProps } from '../../../../services/bi-logger/logger-provider';
import { PERMISSION_DENIED_ALERT, PUSH_SECTION, PUSH_TOGGLE_SWITCH, PUSH_TURN_ON_BUTTON } from '../../../../consts/data-hooks';
import * as style from './style.scss';

export interface PushNotificationsSectionState {
  browserState: NotificationPermission;
  isInteractable: boolean;
  isChecked: boolean;
  openModal: boolean;
  showPermissionDeniedAlert: boolean;
}

interface PushNotificationsSectionProps {
  metaSiteId: string;
}

class PushNotificationsSection extends React.PureComponent<InjectedBiLoggerProps & InjectedTranslateProps & PushNotificationsSectionProps, PushNotificationsSectionState> {
  image = require('../../../../assets/images/push.png');

  readonly state: PushNotificationsSectionState = {
    browserState: 'default',
    isInteractable: false,
    isChecked: false,
    showPermissionDeniedAlert: false,
    openModal: false
  };

  private unregisterFromPermissionModalEvents = () => null;

  componentDidMount() {
    this.disableInteractionWhile(() => this.updateBrowserPermissions());
  }

  private listenToPermissionModalEvents() {
    const unregisterFromModalShowEvent = browserNotificationsClient.registerForBrowserPermissionRequest(() =>
      this.openModalOverlayBackgroundForBrowserPermissionsDialog());
    const unregisterFromModalHideEvent = browserNotificationsClient.registerForBrowserPermissionResponse(() =>
      this.closeModalOverlayBackgroundForBrowserPermissionsDialog());

    this.unregisterFromPermissionModalEvents = () => {
      unregisterFromModalShowEvent();
      unregisterFromModalHideEvent();
    };
  }

  private async updateBrowserPermissions() {
    const { metaSiteId } = this.props;
    this.setState({
      browserState: browserNotificationsClient.getBrowserNotificationsPermission(),
      isChecked: await browserNotificationsClient.isRegisteredForBrowserNotificationsOnSite(metaSiteId)
    });
  }

  private async disableInteractionWhile(func: () => Promise<any>) {
    this.setState({ isInteractable: false });
    await func();
    this.setState({ isInteractable: true });
  }

  async registerForPushNotifications() {
    const { metaSiteId } = this.props;
    try {
      this.listenToPermissionModalEvents();
      await browserNotificationsClient.registerAndTurnOnSite(metaSiteId);
      this.setState({ browserState: 'granted', isChecked: true });
      this.reportToggleChanged(true);
    } catch (err) {
      await this.updateBrowserPermissions();
      this.setState({ openModal: false });
    } finally {
      this.unregisterFromPermissionModalEvents();
    }
  }

  async toggleMuteState() {
    const { metaSiteId } = this.props;
    const { isChecked } = this.state;
    if (!isChecked) {
      await browserNotificationsClient.registerAndTurnOnSite(metaSiteId);
      this.setState({ isChecked: true });
      this.reportToggleChanged(true);
    } else {
      await setUserSettings(metaSiteId, ChannelFilter.BROWSER, true);
      this.setState({ isChecked: false });
      this.reportToggleChanged(false);
    }
  }

  reportPushPermissionsDenied() {
    const { logger } = this.props;
    logger.reportDesktopPushDenied();
  }

  reportFirstTimeBiEvent() {
    const { logger } = this.props;
    logger.reportTurnOnPushForFirstTime();
  }

  reportBrowserPermissionDialog() {
    const { logger } = this.props;
    const permission = browserNotificationsClient.getBrowserNotificationsPermission();
    logger.reportBrowserPermissionToggleDisplayed(permission);
  }

  reportToggleChanged(value: boolean) {
    const { logger } = this.props;
    logger.reportTogglePush(value);
  }

  onTurnOnButtonClicked = async () => {
    let permissionState = browserNotificationsClient.getBrowserNotificationsPermission();
    if (permissionState === 'denied') {
      this.openBlockedPermissionsAlert();
      return;
    } else {
      this.reportFirstTimeBiEvent();
      await this.disableInteractionWhile(() => this.registerForPushNotifications());
      this.reportBrowserPermissionDialog();
    }
  }

  onToggleClicked = async () => {
    await this.disableInteractionWhile(async () => {
      const browserState = browserNotificationsClient.getBrowserNotificationsPermission();
      if (browserState === 'granted') {
        await this.toggleMuteState();
      } else {
        this.setState({ browserState });
      }
    });
  }

  openModalOverlayBackgroundForBrowserPermissionsDialog() {
    this.setState({ openModal: true, showPermissionDeniedAlert: false });
  }

  closeModalOverlayBackgroundForBrowserPermissionsDialog() {
    this.setState({ openModal: false, showPermissionDeniedAlert: false });
  }

  openBlockedPermissionsAlert() {
    this.reportPushPermissionsDenied();
    this.setState({ openModal: true, showPermissionDeniedAlert: true });
  }

  closeBlockedPermissionsAlert() {
    this.setState({ openModal: false, showPermissionDeniedAlert: false });
  }

  renderButtonOrToggle() {
    const { browserState, isInteractable, isChecked } = this.state;
    const { t } = this.props;

    if (browserState === 'granted') {
      return <ToggleSwitch
        dataHook={PUSH_TOGGLE_SWITCH}
        disabled={!isInteractable}
        checked={isChecked}
        onChange={this.onToggleClicked}/>;
    } else {
      return <Button dataHook={PUSH_TURN_ON_BUTTON} onClick={() => this.onTurnOnButtonClicked()} disabled={!isInteractable} size="small" upgrade>
        {t('push.turn-on')}
      </Button>;
    }
  }

  render() {
    const { t } = this.props;
    const { openModal, showPermissionDeniedAlert } = this.state;
    return <section className={style['settings-section']} data-hook={PUSH_SECTION}>
      <img className={style['section-icon']} src={this.image}/>
      <span className={style.content}>
        <Heading appearance="H3">{t('push.title')}</Heading>
        <div className={style['content-body']}>
           <div className={style['content-body-description']}>
              <Text className={style.description} size="medium" weight="thin" secondary>
                {t('push.description')}
                {' '}
                <TextButton as="a" href="https://support.wix.com/en/article/desktop-push-notifications" target="_blank">{t('push.learn-more')}</TextButton>
              </Text>
           </div>
        </div>
      </span>
      <div className={style.contentBodyToggleArea}>
        {this.renderButtonOrToggle()}
      </div>
      <Modal
        isOpen={openModal}
        zIndex={2100}
        contentLabel="desktop notifications alert"
      >
        {showPermissionDeniedAlert && <MessageBoxFunctionalLayout
          dataHook={PERMISSION_DENIED_ALERT}
          title={t('alert.title')}
          onOk={() => this.closeBlockedPermissionsAlert()}
          onClose={() => this.closeBlockedPermissionsAlert()}
          confirmText={t('alert.cta')}
          theme="blue">
          <Text className={style.description} size="medium" weight="thin" secondary>
            {t('alert.content')}&nbsp;
            <TextButton as="a" href="https://support.wix.com/en/article/desktop-push-notifications" target="_blank">{t('alert.link')}</TextButton>
          </Text>

        </MessageBoxFunctionalLayout>}
      </Modal>
    </section>;
  }
}

export const PushSection = provideLogger<PushNotificationsSectionProps>(translate()(PushNotificationsSection));
